import { useState } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import type { RecapKind, RecapRow } from '@busy/api';
import type { RecapData } from '@busy/core';
import { recomputeRecap, useCurrentUserProfile } from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import { RecapPrintLayout } from './RecapPrintLayout';

// Renders a saved recap as a formatted summary screen. Pulls every value out
// of the row.data jsonb so the page mirrors what got emailed at generation
// time — the same RecapData payload that fed the notify_recap trigger.

export function RecapModal({ recap, onClose }: { recap: RecapRow; onClose: () => void }) {
  const t = useT();
  const language = useUI((s) => s.language);
  const locale = language === 'es' ? 'es-US' : 'en-US';
  const profile = useCurrentUserProfile();
  const toast = useToast();
  const data = recap.data as unknown as RecapData;
  const [sharing, setSharing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  /** Refresh: pulls the latest data and UPDATEs the recap's data
   *  payload in place. Useful when the recap schema changed (e.g.
   *  v1 → v2 with tax-prep fields) or when visit edits landed after
   *  the recap was first generated. Doesn't re-trigger the email. */
  async function handleRefresh() {
    setRefreshing(true);
    const result = await recomputeRecap(recap.id);
    setRefreshing(false);
    if (result.error) {
      toast(t('recap.refresh.error', { message: result.error.message }), 'error');
      return;
    }
    toast(t('recap.refresh.done'), 'success');
    // Cache invalidation re-fires the dashboard query; the modal
    // re-renders with the fresh data on next mount. For now, close
    // and let the user re-open to see the updated payload.
    onClose();
  }

  const periodLabel = formatPeriod(recap.period_start, recap.period_end, recap.kind, locale);
  // Eyebrow + "this week / this month / this quarter / this year"
  // copy switch off recap.kind. The annual recap doubles as the
  // tax-prep summary so its eyebrow reads "YEAR IN REVIEW" (set in
  // i18n) rather than "ANNUAL WRAP-UP" to signal that's where to look
  // come tax season.
  const eyebrow =
    recap.kind === 'weekly'
      ? t('recap.eyebrow.weekly')
      : recap.kind === 'monthly'
        ? t('recap.eyebrow.monthly')
        : recap.kind === 'quarterly'
          ? t('recap.eyebrow.quarterly')
          : t('recap.eyebrow.annual');
  const revDollars = Math.round((data.totals?.revenue_cents ?? 0) / 100);

  /**
   * Capture the off-screen RecapPrintLayout as a real PDF and hand it
   * to the native share sheet via the Web Share API. Tax preparers
   * expect PDF, not PNG — iMessage / Mail / AirDrop / Files all
   * preview PDFs natively, and PDFs are universally understood as
   * "document" by accounting software and email clients.
   *
   * Pipeline:
   *   1. html2canvas → render the off-screen DOM to a high-DPI canvas
   *   2. jsPDF       → split the canvas into Letter-sized pages,
   *                    add as an image to each page
   *   3. .output('blob') → application/pdf blob ready for share
   *
   * The previous html-to-image PNG path had issues on some platforms
   * where the receiving app showed a generic icon instead of a preview.
   * PDF avoids that entire class of bug.
   */
  async function handleShare() {
    const node = document.querySelector<HTMLElement>('.recap-print-root');
    if (!node) return;
    setSharing(true);
    try {
      // Wait for any custom fonts (Anton, Inter) to be ready before
      // rendering. html2canvas serializes a font that hasn't finished
      // loading as nothing, producing a blank PDF.
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
      }

      // Read laid-out dimensions. The off-screen .recap-print-root is
      // absolutely-positioned far-off-screen with a fixed 720px width;
      // scrollHeight captures the full content height including overflow.
      const captureWidth = node.offsetWidth || 720;
      const captureHeight = node.scrollHeight || node.offsetHeight || 0;
      if (captureHeight === 0) throw new Error('capture-zero-height');

      // Render to canvas at 2× for retina-quality PDF output.
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        width: captureWidth,
        height: captureHeight,
      });

      // Build a Letter-sized PDF and tile the canvas across pages.
      // Letter at 72dpi = 612×792pt. We render full width per page,
      // adding new pages until the canvas is fully placed. This
      // gracefully handles long recaps (annual with all 12 months +
      // 10 top clients + 10 top services tends to be 2-3 pages).
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter',
        compress: true,
      });
      const pageWidthPt = pdf.internal.pageSize.getWidth();
      const pageHeightPt = pdf.internal.pageSize.getHeight();
      // Margin in points. ~24pt = ~1/3 inch top/bottom margin so the
      // content has breathing room and printers don't clip edges.
      const marginPt = 24;
      const usableWidthPt = pageWidthPt - marginPt * 2;
      // Aspect-preserving scale from canvas px → PDF pt.
      const imgWidthPt = usableWidthPt;
      const imgHeightPt = (canvas.height * imgWidthPt) / canvas.width;
      // The image is placed multiple times with negative Y offsets to
      // simulate page-cut: page 1 shows the top, page 2 shows the next
      // slice, etc. heightLeft tracks how much canvas remains.
      const dataUrl = canvas.toDataURL('image/png');
      let yOffset = marginPt;
      let heightLeft = imgHeightPt;
      pdf.addImage(dataUrl, 'PNG', marginPt, yOffset, imgWidthPt, imgHeightPt, undefined, 'FAST');
      heightLeft -= pageHeightPt - marginPt * 2;
      while (heightLeft > 0) {
        pdf.addPage();
        // Position the next slice so what was below the previous page
        // bottom now lands at the top margin of the new page.
        yOffset = marginPt - (imgHeightPt - heightLeft);
        pdf.addImage(dataUrl, 'PNG', marginPt, yOffset, imgWidthPt, imgHeightPt, undefined, 'FAST');
        heightLeft -= pageHeightPt - marginPt * 2;
      }

      const blob = pdf.output('blob');
      if (!blob || blob.size === 0) throw new Error('pdf-empty');
      const filename = buildFilename(recap, locale);
      const file = new File([blob], filename, { type: 'application/pdf' });

      // Web Share API. canShare() with files first; not all browsers
      // that have navigator.share accept file payloads (Android Chrome
      // older versions, some embedded webviews).
      const navAny = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
      };
      if (
        typeof navigator.share === 'function' &&
        typeof navAny.canShare === 'function' &&
        navAny.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: periodLabel,
          text: `${eyebrow} · ${periodLabel}`,
        });
      } else {
        // Fallback: trigger a download. Desktop browsers + share-less
        // mobile get a real PDF file they can open + attach + email.
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast(t('recap.share.downloaded'), 'success');
      }
    } catch (err: unknown) {
      // AbortError is fired when the user dismisses the share sheet —
      // not a real error, no toast needed.
      const name = (err as { name?: string } | null)?.name;
      if (name !== 'AbortError') {
        toast(t('recap.share.error'), 'error');
      }
    } finally {
      setSharing(false);
    }
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={periodLabel}>
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white p-6 relative rounded-t-2xl">
        <ModalCloseButton onClose={onClose} variant="dark" />
        <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400 mb-2">
          {eyebrow}
        </div>
        <h2 className="font-display text-3xl leading-tight">{periodLabel}</h2>
        <p className="text-white/72 text-sm mt-1.5">
          {t('recap.subhead', {
            kind:
              recap.kind === 'weekly'
                ? t('recap.kind.week')
                : recap.kind === 'monthly'
                  ? t('recap.kind.month')
                  : recap.kind === 'quarterly'
                    ? t('recap.kind.quarter')
                    : t('recap.kind.year'),
          })}
        </p>
      </div>

      <div className="p-5 space-y-5 max-h-[70dvh] overflow-y-auto">
        {/* Total income hero — cuts + tips combined. The tax-prep
            headline number; falls back to revenue_cents (cuts only)
            for v1 recap rows that don't carry the new field. */}
        <TotalIncomeHero data={data} t={t} />

        {/* Headline KPIs — Revenue, Cuts, Tips. Streak / longest-in-
            period moves to SecondaryStats so the top of the recap
            stays focused on the income numbers. */}
        <div className="grid grid-cols-3 gap-2">
          <Kpi label={t('recap.kpi.revenue')} value={`$${revDollars.toLocaleString()}`} change={data.vs_prior?.revenue_change_pct ?? null} />
          <Kpi label={t('recap.kpi.cuts')} value={String(data.totals?.cuts ?? 0)} change={data.vs_prior?.cuts_change_pct ?? null} />
          <Kpi
            label={t('recap.kpi.tips')}
            value={`$${Math.round((data.totals?.tips_cents ?? 0) / 100).toLocaleString()}`}
            change={data.vs_prior?.tips_change_pct ?? null}
            valueClass="text-green-600 dark:text-green-400"
          />
        </div>

        {/* Tax summary block — surfaces the numbers a tax preparer
            actually needs. Only renders for monthly+ recaps OR when
            the new totals are present (v2 recap rows). */}
        <TaxSummary data={data} t={t} />

        {/* Payment method breakdown — 1099-K reconciliation workbook.
            Renders when there's any payment_method captured (skips
            v1 recaps + periods where every cut was "unspecified"). */}
        <PaymentMethodBreakdown data={data} t={t} />

        {/* Sub-period breakdown — monthly recap shows weekly rows,
            quarterly shows monthly rows, annual shows monthly rows
            grouped by quarter. Empty / undefined on weekly recaps. */}
        <SubPeriodBreakdown data={data} t={t} />

        {/* Secondary stats */}
        <SecondaryStats data={data} />

        {/* Top clients */}
        {data.top_clients?.length > 0 && (
          <Section title={t('recap.topClients')}>
            <ul className="space-y-1.5">
              {data.top_clients.map((c) => (
                <li key={c.client_id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{c.name}</span>
                  <span className="text-ink-500 text-xs whitespace-nowrap">
                    {c.visits}× · ${Math.round(c.revenue_cents / 100)}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Top cut types */}
        {data.top_cut_types?.length > 0 && (
          <Section title={t('recap.topCuts')}>
            <ul className="space-y-1.5">
              {data.top_cut_types.map((c) => (
                <li key={c.name} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{c.name}</span>
                  <span className="text-ink-500 text-xs whitespace-nowrap">
                    ×{c.count} · ${Math.round(c.revenue_cents / 100)}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Top add-ons */}
        {data.top_addons?.length > 0 && (
          <Section title={t('recap.topAddons')}>
            <ul className="space-y-1.5">
              {data.top_addons.map((a) => (
                <li key={a.name} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{a.name}</span>
                  <span className="text-ink-500 text-xs whitespace-nowrap">
                    ×{a.count} · ${Math.round(a.revenue_cents / 100)}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Milestones */}
        {data.milestones?.length > 0 && (
          <Section title={t('recap.milestones', { count: data.milestones.length })}>
            <ul className="space-y-1.5">
              {data.milestones.map((m, i) => (
                <li key={`${m.kind}-${m.threshold}-${i}`} className="flex items-center gap-2 text-sm">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold-500" />
                  <span>{t(`recap.milestone.${m.kind}`, { n: m.threshold })}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Best day */}
        {data.best_day && data.best_day.revenue_cents > 0 && (
          <Section title={t('recap.bestDay')}>
            <p className="text-sm text-ink-700">
              {new Date(data.best_day.date + 'T00:00:00').toLocaleDateString(locale, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}{' '}
              · <strong>${Math.round(data.best_day.revenue_cents / 100).toLocaleString()}</strong>
            </p>
          </Section>
        )}

        {/* Email indicator */}
        {recap.email_sent_at && (
          <p className="text-[11px] text-ink-500 leading-relaxed pt-2 border-t border-line">
            {t('recap.emailedAt', {
              when: new Date(recap.email_sent_at).toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
            })}
          </p>
        )}

        {/* Action row — Share PDF + Refresh side-by-side. Refresh
            recomputes the recap payload in place against the latest
            visit data; useful after late visit edits or when a
            recap was generated under a prior schema version. */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleShare}
            disabled={sharing}
            className="flex-1 h-11 rounded-lg border-2 border-navy-900 dark:border-gold-500 text-navy-900 dark:text-gold-500 font-bold inline-flex items-center justify-center gap-2 hover:bg-bg-alt disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <ShareIcon className="w-4 h-4" />
            {sharing ? t('recap.share.busy') : t('recap.share')}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            title={t('recap.refresh.title')}
            className="shrink-0 h-11 px-4 rounded-lg border border-line bg-surface text-ink-700 text-sm font-bold inline-flex items-center gap-1.5 hover:bg-bg-alt disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? t('recap.refresh.busy') : t('recap.refresh')}
          </button>
        </div>
      </div>

      {/* Off-screen render target — positioned via .recap-print-root
          in main.css far off-screen. html-to-image walks this DOM
          subtree to produce the shareable PNG. Rendered into body so
          it sits outside the modal's overlay scaffolding (which uses
          fixed positioning and would distort the capture). */}
      {createPortal(
        <RecapPrintLayout
          recap={recap}
          barberFirstName={profile.data?.first_name ?? null}
        />,
        document.body,
      )}
    </ModalShell>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 16V4" />
      <polyline points="7 9 12 4 17 9" />
      <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    </svg>
  );
}

/** Build a sensible filename for the shared PDF. Used both as the
 *  Web Share API attachment name and the fallback download filename.
 *  Format: "busy-barber-{kind}-{YYYY-MM-DD}.pdf" — PDF extension is
 *  what tax preparers + accounting software expect to receive. */
function buildFilename(recap: RecapRow, _locale: string): string {
  const start = recap.period_start; // already YYYY-MM-DD from supabase
  return `busy-barber-${recap.kind}-${start}.pdf`;
}

function Kpi({
  label,
  value,
  change,
  valueClass,
}: {
  label: string;
  value: string;
  change: number | null;
  /** Override the value color — used by the Tips KPI to render green
   *  instead of navy/gold so tips read consistently across surfaces. */
  valueClass?: string;
}) {
  const t = useT();
  let badge: { text: string; cls: string } | null = null;
  if (change != null) {
    if (change > 0) badge = { text: `+${change}%`, cls: 'bg-green-50 text-green-700 dark:bg-green-600/20 dark:text-green-300' };
    else if (change < 0) badge = { text: `${change}%`, cls: 'bg-red-50 text-red-700 dark:bg-red-500/20 dark:text-red-300' };
    else badge = { text: t('recap.flat'), cls: 'bg-bg-alt text-ink-500' };
  }
  return (
    <div className="rounded-xl bg-bg-alt p-3 text-center">
      <div className={`font-display text-2xl leading-none tabular-nums ${valueClass ?? 'text-navy-900 dark:text-gold-500'}`}>{value}</div>
      <div className="text-[10px] tracking-[0.12em] uppercase font-bold text-ink-500 mt-1.5">{label}</div>
      {badge && (
        <div className={`inline-block text-[10px] font-extrabold px-1.5 py-0.5 rounded mt-1 ${badge.cls}`}>
          {badge.text}
        </div>
      )}
    </div>
  );
}

/** Total income hero — cuts + tips combined. The tax-prep headline.
 *  Falls back to revenue_cents for v1 recap rows that don't carry
 *  the new total_income_cents field. */
function TotalIncomeHero({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const totalIncome =
    data.totals?.total_income_cents ??
    (data.totals?.revenue_cents ?? 0) + (data.totals?.tips_cents ?? 0);
  const revenueDollars = Math.round((data.totals?.revenue_cents ?? 0) / 100);
  const tipsDollars = Math.round((data.totals?.tips_cents ?? 0) / 100);
  return (
    <div className="rounded-2xl bg-gradient-to-br from-gold-500/15 to-gold-500/5 dark:from-gold-500/20 dark:to-gold-500/10 border border-gold-500/40 p-4">
      <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-navy-900/80 dark:text-gold-300 mb-1">
        {t('recap.hero.totalIncome')}
      </div>
      <div className="font-display text-4xl text-navy-900 dark:text-gold-500 tabular-nums leading-none">
        ${(totalIncome / 100).toLocaleString()}
      </div>
      <div className="text-[12px] text-ink-700 dark:text-ink-300 mt-2 tabular-nums">
        {t('recap.hero.breakdown', {
          cuts: revenueDollars.toLocaleString(),
          tips: tipsDollars.toLocaleString(),
        })}
      </div>
    </div>
  );
}

/** Tax summary block — renders when the v2 totals are present.
 *  Surfaces the fields a tax preparer actually wants: gross income
 *  (cuts + tips), gross from services, gross from add-ons, total
 *  tips, distinct clients served, days worked, and discounts given.
 *  All optional in v1 recaps; falls back to nothing when missing. */
function TaxSummary({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const hasV2 =
    data.totals?.total_income_cents != null ||
    data.totals?.service_revenue_cents != null ||
    data.totals?.days_worked != null;
  if (!hasV2) return null;
  const rows: Array<{ label: string; value: string; tone?: 'tip' | 'discount' }> = [];
  const totalIncome = data.totals?.total_income_cents ??
    (data.totals?.revenue_cents ?? 0) + (data.totals?.tips_cents ?? 0);
  rows.push({
    label: t('recap.tax.totalIncome'),
    value: `$${Math.round(totalIncome / 100).toLocaleString()}`,
  });
  if ((data.totals?.service_revenue_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.serviceRevenue'),
      value: `$${Math.round((data.totals?.service_revenue_cents ?? 0) / 100).toLocaleString()}`,
    });
  }
  if ((data.totals?.addon_revenue_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.addonRevenue'),
      value: `$${Math.round((data.totals?.addon_revenue_cents ?? 0) / 100).toLocaleString()}`,
    });
  }
  if ((data.totals?.tips_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.tipsTotal'),
      value: `$${Math.round((data.totals?.tips_cents ?? 0) / 100).toLocaleString()}`,
      tone: 'tip',
    });
  }
  if ((data.totals?.distinct_clients ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.distinctClients'),
      value: String(data.totals?.distinct_clients ?? 0),
    });
  }
  if ((data.totals?.days_worked ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.daysWorked'),
      value: String(data.totals?.days_worked ?? 0),
    });
  }
  // Hours worked — displayed as fractional hours with one decimal.
  if ((data.totals?.hours_worked_tenths ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.hoursWorked'),
      value: `${((data.totals?.hours_worked_tenths ?? 0) / 10).toFixed(1)}h`,
    });
  }
  if ((data.totals?.avg_daily_income_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.avgDailyIncome'),
      value: `$${Math.round((data.totals?.avg_daily_income_cents ?? 0) / 100).toLocaleString()}`,
    });
  }
  // Earning rate — income per hour worked. Self-employment benchmark.
  if ((data.totals?.earning_rate_cents_per_hour ?? null) != null) {
    rows.push({
      label: t('recap.tax.earningRate'),
      value: `$${Math.round((data.totals?.earning_rate_cents_per_hour ?? 0) / 100).toLocaleString()}/hr`,
    });
  }
  // Transaction range — pricing min/max across the period. Only
  // surfaced when min ≠ max so a single-rate barber doesn't see a
  // useless "$X to $X" line.
  const minT = data.totals?.min_ticket_cents ?? 0;
  const maxT = data.totals?.max_ticket_cents ?? 0;
  if (maxT > 0 && minT !== maxT) {
    rows.push({
      label: t('recap.tax.ticketRange'),
      value: `$${Math.round(minT / 100)} – $${Math.round(maxT / 100)}`,
    });
  }
  if ((data.totals?.discount_given_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.discountGiven'),
      value: `$${Math.round((data.totals?.discount_given_cents ?? 0) / 100).toLocaleString()}`,
      tone: 'discount',
    });
  }
  // Walk-in vs appointment mix (migration 0072). Only renders when the
  // barber tagged at least one cut in the period — visits without a
  // was_appointment value are excluded from the denominator so the %
  // reflects intentional tagging. Useful for tax prep as a "client
  // acquisition channel" cue and for the barber's own pricing /
  // marketing decisions ("75% walk-ins = invest in signage / window
  // visibility; 75% appointments = optimize booking funnel").
  const walkinPct = data.totals?.walkin_pct ?? null;
  const taggedTotal =
    (data.totals?.walkin_count ?? 0) + (data.totals?.appointment_count ?? 0);
  if (walkinPct != null && taggedTotal > 0) {
    rows.push({
      label: t('recap.tax.walkinMix'),
      value: t('recap.tax.walkinMixValue', {
        walkinPct,
        apptPct: 100 - walkinPct,
        walkin: data.totals?.walkin_count ?? 0,
        appt: data.totals?.appointment_count ?? 0,
      }),
    });
  }
  // Activity window — first / last visit date. Confirms the reporting
  // window matches actual business activity.
  const firstAt = data.period_activity?.first_visit_at;
  const lastAt = data.period_activity?.last_visit_at;
  let activityLine: string | null = null;
  if (firstAt && lastAt) {
    const f = new Date(firstAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const l = new Date(lastAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    activityLine = f === l ? f : `${f} – ${l}`;
  }
  if (activityLine) {
    rows.push({ label: t('recap.tax.activityWindow'), value: activityLine });
  }
  return (
    <section>
      <h3 className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2 flex items-center gap-1.5">
        <ReceiptIcon className="w-3 h-3" />
        {t('recap.tax.heading')}
      </h3>
      <div className="rounded-xl border border-line bg-surface px-4 py-3">
        <ul className="space-y-1.5 text-sm">
          {rows.map((r) => (
            <li key={r.label} className="flex items-center justify-between gap-2">
              <span className="text-ink-500">{r.label}</span>
              <strong
                className={`font-bold tabular-nums ${
                  r.tone === 'tip'
                    ? 'text-green-600 dark:text-green-400'
                    : r.tone === 'discount'
                      ? 'text-red-600 dark:text-red-400'
                      : ''
                }`}
              >
                {r.value}
              </strong>
            </li>
          ))}
        </ul>
        <p className="text-[10px] text-ink-500 mt-2 leading-snug">
          {t('recap.tax.footnote')}
        </p>
      </div>
    </section>
  );
}

/** Per-payment-method breakdown of revenue + tips + cuts.
 *  Tax-prep purpose: each 1099-K processor (Square, Venmo, Cash App,
 *  etc.) reports independently to the IRS, so the barber needs their
 *  app-tracked totals split by method to reconcile against each
 *  form they receive at year-end.
 *
 *  Skips entirely when no payment_method has ever been captured in
 *  the period (single "unspecified" row with the period's total).
 *  Rows with zero activity are omitted from display, but the
 *  underlying payload preserves all 7 keys for shape stability. */
function PaymentMethodBreakdown({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const rows = data.payment_method_breakdown;
  if (!rows || rows.length === 0) return null;
  const active = rows.filter((r) => r.cuts > 0);
  if (active.length === 0) return null;
  // If 100% of activity was "unspecified" (no payment_method captured
  // anywhere in the period — typical for users who haven't updated
  // since 0071), the breakdown is just one all-period row, which is
  // redundant with the tax-summary block above. Skip it.
  if (active.length === 1 && active[0]?.method === 'unspecified') return null;
  return (
    <section>
      <h3 className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2 flex items-center gap-1.5">
        <CardIcon className="w-3 h-3" />
        {t('recap.payment.heading')}
      </h3>
      <div className="rounded-xl border border-line bg-surface overflow-hidden">
        <ul>
          {active.map((r) => {
            const income = Math.round(r.total_income_cents / 100);
            const cuts = r.cuts;
            const pct = r.share_pct ?? 0;
            return (
              <li
                key={r.method}
                className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm border-b border-line last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <strong className="block leading-tight">
                    {t(`payment.${r.method}`)}
                  </strong>
                  <span className="text-[11px] text-ink-500">
                    {t('recap.payment.cutsLine', { cuts })}
                  </span>
                </div>
                <div className="text-right shrink-0 tabular-nums">
                  <div className="font-bold text-ink-900">
                    ${income.toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-ink-500">
                    {pct}%
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="text-[10px] text-ink-500 px-3 py-2 leading-snug bg-bg-alt">
          {t('recap.payment.footnote')}
        </p>
      </div>
    </section>
  );
}

/** Sub-period breakdown — weekly (in monthly recap), monthly (in
 *  quarterly + annual recaps). Annual recaps group monthly rows by
 *  quarter for tax-prep readability. */
function SubPeriodBreakdown({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const rows = data.sub_period_breakdowns;
  if (!rows || rows.length === 0) return null;

  // Group by quarter for annual recaps so the layout reads like a tax
  // worksheet (Q1: Jan/Feb/Mar; Q2: ...). For quarterly + monthly
  // recaps the rows render flat — too few to need grouping.
  if (data.kind === 'annual') {
    const byQuarter = new Map<number, typeof rows>();
    for (const r of rows) {
      const month = new Date(r.period_start + 'T00:00:00').getMonth(); // 0-11
      const q = Math.floor(month / 3) + 1;
      const arr = byQuarter.get(q) ?? [];
      arr.push(r);
      byQuarter.set(q, arr);
    }
    return (
      <section>
        <h3 className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2">
          {t('recap.breakdown.headingMonthly')}
        </h3>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((q) => {
            const months = byQuarter.get(q);
            if (!months || months.length === 0) return null;
            const qTotal = months.reduce((s, m) => s + m.total_income_cents, 0);
            return (
              <div key={q} className="rounded-xl border border-line bg-surface overflow-hidden">
                <div className="bg-bg-alt px-3 py-2 flex items-center justify-between text-[11px] font-extrabold tracking-[0.12em] uppercase text-navy-900 dark:text-gold-400">
                  <span>{t('recap.breakdown.quarterLabel', { q })}</span>
                  <span className="tabular-nums text-ink-700">
                    ${Math.round(qTotal / 100).toLocaleString()}
                  </span>
                </div>
                <ul>
                  {months.map((m) => (
                    <BreakdownRow key={m.period_start} row={m} t={t} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  const heading =
    data.kind === 'monthly'
      ? t('recap.breakdown.headingWeekly')
      : t('recap.breakdown.headingMonthly');
  return (
    <section>
      <h3 className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2">
        {heading}
      </h3>
      <div className="rounded-xl border border-line bg-surface overflow-hidden">
        <ul>
          {rows.map((r) => (
            <BreakdownRow key={r.period_start} row={r} t={t} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function BreakdownRow({
  row,
  t,
}: {
  row: NonNullable<RecapData['sub_period_breakdowns']>[number];
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const income = Math.round(row.total_income_cents / 100);
  const tips = Math.round(row.tips_cents / 100);
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm border-b border-line last:border-0">
      <div className="min-w-0">
        <strong className="block leading-tight">{row.label}</strong>
        <span className="text-[11px] text-ink-500">
          {t('recap.breakdown.metaLine', {
            cuts: row.cuts,
            clients: row.distinct_clients,
          })}
        </span>
      </div>
      <div className="text-right shrink-0 tabular-nums">
        <div className="font-bold text-ink-900">${income.toLocaleString()}</div>
        {tips > 0 && (
          <div className="text-[10px] text-green-600 dark:text-green-400 font-bold">
            {t('recap.breakdown.tipsSub', { tips: tips.toLocaleString() })}
          </div>
        )}
      </div>
    </li>
  );
}

function CardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="7" y1="15" x2="11" y2="15" />
    </svg>
  );
}

function ReceiptIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 V2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="16" y2="11" />
      <line x1="8" y1="15" x2="13" y2="15" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  );
}

function SecondaryStats({ data }: { data: RecapData }) {
  const t = useT();
  const rows: Array<{ label: string; value: string; tone?: 'tip' | 'highlight' }> = [];
  // Streak — period-bounded longest run of lesson-view days. Moved
  // here from the headline KPI grid (which now leads with income).
  const streakInPeriod =
    data.streak?.longest_in_period ?? data.streak?.current ?? 0;
  if (streakInPeriod > 0) {
    rows.push({
      label: t('recap.row.streakInPeriod'),
      value: t(
        streakInPeriod === 1 ? 'recap.row.streak.days_one' : 'recap.row.streak.days_other',
        { n: streakInPeriod },
      ),
      tone: 'highlight',
    });
  }
  if ((data.totals?.new_clients ?? 0) > 0) {
    rows.push({ label: t('recap.row.newClients'), value: String(data.totals.new_clients) });
  }
  if ((data.totals?.avg_ticket_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.row.avgTicket'),
      value: `$${Math.round(data.totals.avg_ticket_cents / 100)}`,
    });
  }
  // Tip rate + attach rate (RATES — dollar totals live in TaxSummary
  // so we don't duplicate). Captures generosity scaled to ticket size
  // + the fraction of visits that produced any tip.
  if ((data.totals?.tip_rate_pct ?? null) != null) {
    rows.push({
      label: t('recap.row.tipRate'),
      value: `${data.totals?.tip_rate_pct ?? 0}%`,
      tone: 'tip',
    });
  }
  if ((data.totals?.tip_attach_rate_pct ?? null) != null) {
    rows.push({
      label: t('recap.row.tipAttachRate'),
      value: `${data.totals?.tip_attach_rate_pct ?? 0}%`,
      tone: 'tip',
    });
  }
  if ((data.totals?.chair_time_min ?? 0) > 0) {
    const h = Math.floor(data.totals.chair_time_min / 60);
    const m = data.totals.chair_time_min % 60;
    rows.push({
      label: t('recap.row.chairTime'),
      value: h > 0 ? `${h}h ${m}m` : `${m}m`,
    });
  }
  if ((data.totals?.addon_attach_count ?? 0) > 0 && (data.totals?.cuts ?? 0) > 0) {
    const pct = Math.round(((data.totals.addon_attach_count ?? 0) / (data.totals.cuts ?? 1)) * 100);
    rows.push({ label: t('recap.row.addonAttach'), value: `${pct}%` });
  }
  if ((data.lessons?.completed ?? 0) > 0) {
    rows.push({ label: t('recap.row.lessons'), value: String(data.lessons.completed) });
  }
  if (rows.length === 0) return null;
  return (
    <div className="rounded-xl border border-line bg-surface px-4 py-3">
      <ul className="space-y-1.5 text-sm">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-2">
            <span className="text-ink-500">{r.label}</span>
            <strong
              className={`font-bold tabular-nums ${
                r.tone === 'tip'
                  ? 'text-green-600 dark:text-green-400'
                  : r.tone === 'highlight'
                    ? 'text-navy-900 dark:text-gold-500'
                    : ''
              }`}
            >
              {r.value}
            </strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

function formatPeriod(start: string, end: string, kind: RecapKind, locale: string): string {
  const startD = new Date(start + 'T00:00:00');
  const endD = new Date(end + 'T00:00:00');
  if (kind === 'annual') {
    // "2025" — the year the recap covers (period_start.year).
    return String(startD.getFullYear());
  }
  if (kind === 'quarterly') {
    // "Q1 2026" — derived from month / 3 + 1.
    const q = Math.floor(startD.getMonth() / 3) + 1;
    return `Q${q} ${startD.getFullYear()}`;
  }
  if (kind === 'monthly') {
    return startD.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  // Weekly: "May 5 – May 11, 2026"
  const sameMonth = startD.getMonth() === endD.getMonth();
  if (sameMonth) {
    const month = startD.toLocaleDateString(locale, { month: 'short' });
    return `${month} ${startD.getDate()} – ${endD.getDate()}, ${endD.getFullYear()}`;
  }
  const startLabel = startD.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const endLabel = endD.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  return `${startLabel} – ${endLabel}, ${endD.getFullYear()}`;
}
